import { useEffect, useState, useRef, useCallback } from 'react';
import { getSDK, type AuthUser, type Asset } from '@stratos-wallet/sdk';
import TokenIcon from './TokenIcon';
import { skipGoApi, CHAIN_ID_MAP, type SkipGoRoute } from './skipGoApi';
import { swapService, type SwapQuote } from './swapService';
import { getProviderName } from './swapRouter';
import { setTestnet, getSdkNetwork, getSolanaSdkNetwork, getTronSdkNetwork } from './networkConfig';
import { isMockRoute, buildMockSteps, type MockStep, type EVMStep, type CantonStep, type SpliceTransferStep, type BridgeReleaseCCStep, type BridgeReleaseUSDCStep } from './testnetMockProvider';
import {
  getBridgeOperatorParty,
  POOL_ADDRESSES,
  TESTNET_TOKENS,
  fetchPoolBalances,
  fetchPendingBridgeContracts,
  autoAcceptPendingContracts,
  fetchCantonVaultBalances,
  setUsdcHoldingTemplateId,
  type PoolBalanceInfo,
  type BridgeContract,
  type CantonVaultBalance,
} from './testnetPoolConfig';
import './App.css';

interface SwapState {
  fromToken: string;
  fromChain: string;
  toToken: string;
  toChain: string;
  fromAmount: string;
  toAmount: string;
}

interface SwapSettings {
  slippage: number;
  deadline: number; // minutes
}

// Mock exchange rates (in production, fetch from DEX aggregator)
const BASE_RATES: Record<string, number> = {
  'CC': 1.25,
  'cUSDC': 1,
  'ETH': 3450,
  'WETH': 3450,
  'BTC': 97500,
  'WBTC': 97450,
  'USDC': 1,
  'USDT': 1,
  'DAI': 1,
  'SOL': 235,
  'MATIC': 0.52,
  'ARB': 0.95,
  'OP': 2.15,
  'AVAX': 42,
  'BNB': 645,
  'LINK': 18.5,
  'UNI': 12.8,
  'AAVE': 185,
};

// Mock liquidity pools
const LIQUIDITY_POOLS: Record<string, { tvl: number; apr: number }> = {
  'CC-USDC': { tvl: 2500000, apr: 12.5 },
  'ETH-USDC': { tvl: 85000000, apr: 4.2 },
  'BTC-USDC': { tvl: 45000000, apr: 3.8 },
  'SOL-USDC': { tvl: 12000000, apr: 8.5 },
  'ETH-WBTC': { tvl: 35000000, apr: 2.1 },
  'MATIC-ETH': { tvl: 8000000, apr: 6.3 },
};

function App() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [swap, setSwap] = useState<SwapState>({
    fromToken: '',
    fromChain: '',
    toToken: '',
    toChain: '',
    fromAmount: '',
    toAmount: '',
  });
  const [swapLoading, setSwapLoading] = useState(false);
  const [swapResult, setSwapResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showFromTokenList, setShowFromTokenList] = useState(false);
  const [showToTokenList, setShowToTokenList] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState<SwapSettings>({
    slippage: 0.5,
    deadline: 20,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedAsset, setExpandedAsset] = useState<string | null>(null); // Track which asset's chain menu is open
  const [testnetMode, setTestnetMode] = useState(false); // Tracks if wallet is on testnet
  const [showTestnetInfo, setShowTestnetInfo] = useState(false);
  const [bridgeParty] = useState(getBridgeOperatorParty(''));

  // Vault balance monitoring
  const [poolBalances, setPoolBalances] = useState<PoolBalanceInfo[]>([]);
  const [bridgeContracts, setBridgeContracts] = useState<BridgeContract[]>([]);
  const [cantonVault, setCantonVault] = useState<CantonVaultBalance>({ ccBalance: '—', usdcBalance: '—' });
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [autoAcceptStatus, setAutoAcceptStatus] = useState('');

  // Quote state (supports multiple providers)
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [currentQuote, setCurrentQuote] = useState<SwapQuote | null>(null);
  const [currentRoute, setCurrentRoute] = useState<SkipGoRoute | null>(null); // For Skip Go execution
  const quoteDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sdk = getSDK({ debug: true });

  // Get decimals for an asset on a specific chain
  const getAssetDecimals = (symbol: string, chain: string): number => {
    const asset = assets.find(a => a.symbol === symbol);
    if (!asset) return 18; // Default to 18

    const chainInfo = asset.chains?.find(c => c.chain === chain);
    if (chainInfo?.decimals) {
      return chainInfo.decimals;
    }

    return 18; // Default
  };

  const getExchangeRate = (from: string, to: string): number => {
    const fromRate = BASE_RATES[from] || 1;
    const toRate = BASE_RATES[to] || 1;
    return fromRate / toRate;
  };

  const getPriceImpact = (amount: number, from: string, to: string): number => {
    // Simulate price impact based on amount
    const poolKey = `${from}-${to}`;
    const reversePoolKey = `${to}-${from}`;
    const pool = LIQUIDITY_POOLS[poolKey] || LIQUIDITY_POOLS[reversePoolKey];
    if (!pool) return 0.01;

    const amountUsd = amount * (BASE_RATES[from] || 1);
    return Math.min((amountUsd / pool.tvl) * 100, 15);
  };

  useEffect(() => {
    if (window.parent === window) {
      setError('This app must be embedded in the Stratos portal');
      setLoading(false);
      return;
    }

    const applyNetwork = (network: string) => {
      if (network === 'testnet') {
        setTestnet(true);
        setTestnetMode(true);
        console.log('[network] TESTNET MODE ACTIVATED');
      } else {
        setTestnet(false);
        setTestnetMode(false);
        console.log('[network] MAINNET MODE');
      }
    };

    const init = async () => {
      try {
        const state = await sdk.connect();
        console.log('[init] Connected:', state.connected, 'network:', (state as any).network);
        setConnected(state.connected);
        setUser(state.user);

        // Detect network from connect response
        if ((state as any).network) {
          applyNetwork((state as any).network);
        } else {
          // Fallback: query explicitly
          try {
            const network = await sdk.getNetwork();
            applyNetwork(network);
          } catch (e) {
            console.warn('[network] getNetwork not available:', e);
          }
        }

        if (state.connected) {
          const assetsData = await sdk.getAssets();
          setAssets(assetsData);

          // Fetch portal config (USDC template ID, etc.) for Canton vault queries
          try {
            const config = await sdk.getConfig();
            if (config.usdcTemplateId) {
              setUsdcHoldingTemplateId(config.usdcTemplateId);
              console.log('[config] USDC template ID:', config.usdcTemplateId);
            }
          } catch (e) {
            console.warn('[config] getConfig not available:', e);
          }

          // On testnet, ensure current user has actAs/readAs on bridge operator party
          // grantUserRights is idempotent — safe to call even if already granted
          const detectedNetwork = (state as any).network;
          if (detectedNetwork === 'testnet' && state.user?.partyId && bridgeParty) {
            sdk.grantUserRights({
              userId: state.user.partyId,
              rights: [
                { type: 'actAs', party: bridgeParty },
                { type: 'readAs', party: bridgeParty },
              ],
            })
              .then(() => console.log('[bridge] User has actAs/readAs on bridge party'))
              .catch((e: Error) => console.warn('[bridge] Grant bridge rights:', e.message));
          }
        }
      } catch (err) {
        console.error('[init] Failed:', err);
        setError(err instanceof Error ? err.message : 'Failed to connect');
      } finally {
        setLoading(false);
      }
    };

    init();

    // Listen for network changes
    const handleNetworkChange = (event: MessageEvent) => {
      const msg = event.data;
      if (msg?.type === 'wallet_event' && msg?.event === 'networkChanged') {
        console.log('[network] Network changed to:', msg.data);
        applyNetwork(msg.data);
      }
    };
    window.addEventListener('message', handleNetworkChange);

    sdk.on('assetsChanged', setAssets);
    sdk.on('userChanged', (newUser) => {
      setUser(newUser);
      setConnected(newUser !== null);
    });

    return () => {
      sdk.removeAllListeners();
      window.removeEventListener('message', handleNetworkChange);
    };
  }, []);

  // Refresh vault balances (EVM pools + Canton bridge contracts + Canton vault)
  // Also runs auto-accept on pending bridge contracts
  const refreshVaultBalances = useCallback(async () => {
    setBalancesLoading(true);

    // Run auto-accept first (accept pending escrows/requests/offers)
    if (bridgeParty) {
      try {
        const result = await autoAcceptPendingContracts(sdk);
        if (result.accepted > 0) {
          setAutoAcceptStatus(`Accepted ${result.accepted} contract(s)`);
        } else if (result.errors.length > 0) {
          setAutoAcceptStatus(`Errors: ${result.errors[0]}`);
        } else {
          setAutoAcceptStatus('No pending contracts');
        }
      } catch (e: unknown) {
        setAutoAcceptStatus(`Error: ${(e as Error).message}`);
      }
    }

    // Then fetch fresh balances
    const [pools, contracts, canton] = await Promise.all([
      fetchPoolBalances(sdk).catch(() => [] as PoolBalanceInfo[]),
      fetchPendingBridgeContracts(sdk).catch(() => [] as BridgeContract[]),
      fetchCantonVaultBalances(sdk).catch((e) => {
        console.warn('[vaultBalances] Canton fetch failed:', e);
        return { ccBalance: '?', usdcBalance: '?' } as CantonVaultBalance;
      }),
    ]);
    setPoolBalances(pools);
    setBridgeContracts(contracts);
    setCantonVault(canton);
    setBalancesLoading(false);
  }, [bridgeParty]);

  // Auto-accept runs when Refresh is clicked (inside refreshVaultBalances)
  // No polling — user triggers it manually

  // Check if asset has multiple chains
  const hasMultipleChains = (asset: Asset): boolean => {
    return (asset.chains?.length ?? 0) > 1;
  };

  // Get available chains for an asset
  const getAssetChains = (asset: Asset): { chain: string; chainType: string; balance: number }[] => {
    if (asset.chains && asset.chains.length > 0) {
      return asset.chains.map(c => ({
        chain: c.chain,
        chainType: c.chainType,
        balance: asset.chainBalances?.[c.chain] ?? 0,
      }));
    }
    return [{
      chain: asset.chain || 'Unknown',
      chainType: asset.chainType || '',
      balance: asset.balance,
    }];
  };

  // Get balance for a specific token+chain
  const getChainBalance = (symbol: string, chain: string): number => {
    const asset = assets.find(a => a.symbol === symbol);
    if (!asset) return 0;
    if (asset.chainBalances?.[chain] !== undefined) {
      return asset.chainBalances[chain];
    }
    if (asset.chain === chain) {
      return asset.balance;
    }
    return 0;
  };

  // Set initial tokens once assets are loaded
  useEffect(() => {
    if (assets.length > 0 && !swap.fromToken && !swap.toToken) {
      const firstAsset = assets[0];
      const firstChains = getAssetChains(firstAsset);
      const secondAsset = assets.find(a => a.symbol !== firstAsset.symbol);
      const secondChains = secondAsset ? getAssetChains(secondAsset) : [];

      setSwap(s => ({
        ...s,
        fromToken: firstAsset.symbol,
        fromChain: firstChains[0]?.chain || '',
        toToken: secondAsset?.symbol || '',
        toChain: secondChains[0]?.chain || '',
      }));
    }
  }, [assets, swap.fromToken, swap.toToken]);

  // Fetch quote from appropriate provider (Skip Go, Jupiter, STON.fi, SunSwap)
  const fetchQuote = useCallback(async (
    fromToken: string,
    fromChain: string,
    toToken: string,
    toChain: string,
    amount: string
  ) => {
    if (!fromToken || !toToken || !amount || parseFloat(amount) <= 0) {
      setCurrentQuote(null);
      setCurrentRoute(null);
      setQuoteError(null);
      return;
    }

    // Check if route is supported
    if (!swapService.isSupported(fromChain, toChain)) {
      setQuoteError(swapService.getUnsupportedReason(fromChain, toChain));
      setCurrentQuote(null);
      setCurrentRoute(null);
      return;
    }

    setQuoteLoading(true);
    setQuoteError(null);

    try {
      const fromDecimals = getAssetDecimals(fromToken, fromChain);
      const toDecimals = getAssetDecimals(toToken, toChain);

      // Get contract addresses if available
      const fromAsset = assets.find(a => a.symbol === fromToken);
      const toAsset = assets.find(a => a.symbol === toToken);
      const fromContract = fromAsset?.chains?.find(c => c.chain === fromChain)?.contractAddress || undefined;
      const toContract = toAsset?.chains?.find(c => c.chain === toChain)?.contractAddress || undefined;

      const quote = await swapService.getQuote({
        fromSymbol: fromToken,
        fromChain,
        toSymbol: toToken,
        toChain,
        amount,
        fromDecimals,
        toDecimals,
        fromContractAddress: fromContract,
        toContractAddress: toContract,
        slippagePercent: settings.slippage,
      });

      setCurrentQuote(quote);

      // If Skip Go (non-mock), also store the route for execution
      if (quote.provider === 'skipgo' && quote.route && !isMockRoute(quote.route)) {
        setCurrentRoute(quote.route as SkipGoRoute);
      } else {
        setCurrentRoute(null);
      }

      // Update toAmount from quote
      setSwap(s => ({ ...s, toAmount: quote.amountOut }));

    } catch (err) {
      console.error('Quote error:', err);
      const message = err instanceof Error ? err.message : 'Failed to get quote';

      // Check for common errors
      if (message.includes('no routes found') || message.includes('not found') || message.includes('unsupported')) {
        setQuoteError('Route not available for this pair');
      } else if (message.includes('insufficient liquidity')) {
        setQuoteError('Insufficient liquidity');
      } else if (message.includes('Unknown') && message.includes('token')) {
        setQuoteError('Token not supported on this chain');
      } else {
        setQuoteError(message || 'Route not available');
      }
      setCurrentQuote(null);
      setCurrentRoute(null);
      setSwap(s => ({ ...s, toAmount: '' }));
    } finally {
      setQuoteLoading(false);
    }
  }, [assets, settings.slippage]);

  const handleFromAmountChange = (value: string) => {
    setSwap(s => ({ ...s, fromAmount: value }));

    // Clear previous debounce
    if (quoteDebounceRef.current) {
      clearTimeout(quoteDebounceRef.current);
    }

    // Don't fetch if empty or invalid
    if (!value || parseFloat(value) <= 0) {
      setSwap(s => ({ ...s, toAmount: '' }));
      setCurrentQuote(null);
      setCurrentRoute(null);
      setQuoteError(null);
      return;
    }

    // Debounce the quote fetch
    quoteDebounceRef.current = setTimeout(() => {
      fetchQuote(swap.fromToken, swap.fromChain, swap.toToken, swap.toChain, value);
    }, 500);
  };

  const handleToAmountChange = (value: string) => {
    // For now, just update the value (reverse quotes not implemented)
    setSwap(s => ({ ...s, toAmount: value }));
  };

  const handleSwapTokens = () => {
    const newSwap = {
      fromToken: swap.toToken,
      fromChain: swap.toChain,
      toToken: swap.fromToken,
      toChain: swap.fromChain,
      fromAmount: swap.toAmount,
      toAmount: swap.fromAmount,
    };
    setSwap(newSwap);
    setCurrentQuote(null);
    setCurrentRoute(null);
    setQuoteError(null);

    // Fetch new quote if there's an amount
    if (newSwap.fromAmount && parseFloat(newSwap.fromAmount) > 0) {
      fetchQuote(newSwap.fromToken, newSwap.fromChain, newSwap.toToken, newSwap.toChain, newSwap.fromAmount);
    }
  };

  const handleSwap = async () => {
    if (!swap.fromToken || !swap.toToken || !swap.fromAmount || !swap.fromChain) return;

    // Check if we have a valid quote
    if (!currentQuote) {
      setSwapResult({
        success: false,
        message: quoteError || 'No route available for this swap',
      });
      return;
    }

    setSwapLoading(true);
    setSwapResult(null);

    try {
      // Handle testnet mock routes — execute real transactions via pool contract
      if (isMockRoute(currentQuote.route)) {
        try {
          // Get user's EVM address
          const addresses = await sdk.getAddresses();
          const evmAddr = addresses.find((a: any) => a.chainType === 'evm')?.address;
          if (!evmAddr) throw new Error('No EVM wallet address found');

          const steps = buildMockSteps(currentQuote, evmAddr);
          let lastTxHash = '';
          const userParty = user?.partyId || '';
          const currentBridgeParty = getBridgeOperatorParty(userParty);

          for (let i = 0; i < steps.length; i++) {
            const step: MockStep = steps[i];
            setSwapResult({
              success: true,
              message: `Step ${i + 1}/${steps.length}: ${step.description}...`,
            });

            if (step.type === 'cantonCreate') {
              const cantonStep = step as CantonStep;
              // Replace party placeholders + inject lastTxHash for __PENDING__
              const payload = JSON.parse(
                JSON.stringify(cantonStep.payload)
                  .replace(/__BRIDGE_PARTY__/g, currentBridgeParty)
                  .replace(/__PARTY__/g, userParty)
                  .replace(/__PENDING__/g, lastTxHash || `pending-${Date.now()}`)
              );
              await sdk.cantonCreate({
                templateId: cantonStep.templateId,
                payload,
              });
            } else if (step.type === 'evm') {
              const evmStep = step as EVMStep;
              const result = await sdk.sendEVMTransaction({
                transaction: {
                  to: evmStep.to,
                  data: evmStep.data,
                  value: evmStep.value,
                  chainId: evmStep.chainId,
                  gasLimit: '0x493E0', // 300,000 gas for contract calls
                },
              });
              lastTxHash = result.transactionHash || '';
            } else if (step.type === 'spliceTransfer') {
              const spliceStep = step as SpliceTransferStep;
              // Replace bridge party placeholder
              const to = spliceStep.to.replace(/__BRIDGE_PARTY__/g, currentBridgeParty);
              await sdk.transfer({
                to,
                amount: spliceStep.amount,
                symbol: spliceStep.symbol,
                chain: 'canton',
              });
            } else if (step.type === 'bridgeProcess') {
              // CC was already auto-accepted at transfer time by the backend.
              // Just exercise any pending BridgeEscrow/BridgeRequest contracts.
              await autoAcceptPendingContracts(sdk);
            } else if (step.type === 'bridgeReleaseCC') {
              const releaseStep = step as BridgeReleaseCCStep;
              const recipientParty = releaseStep.recipientParty.replace(/__PARTY__/g, userParty);
              // Backend creates the TransferOffer AND auto-accepts it for the user
              await sdk.callAPI('/api/canton/bridge-release-cc', {
                amount: releaseStep.amount,
                recipientParty,
              });
            } else if (step.type === 'bridgeReleaseUSDC') {
              const releaseStep = step as BridgeReleaseUSDCStep;
              const recipientParty = releaseStep.recipientParty.replace(/__PARTY__/g, userParty);
              await sdk.callAPI('/api/canton/bridge-release-usdc', {
                amount: releaseStep.amount,
                recipientParty,
              });
            }
          }

          // Refresh assets + vault balances after real on-chain activity
          const assetsData = await sdk.getAssets();
          setAssets(assetsData);
          refreshVaultBalances();

          const hashSuffix = lastTxHash ? ` (tx: ${lastTxHash.slice(0, 14)}...)` : '';
          setSwapResult({
            success: true,
            message: `[TESTNET] Swapped ${currentQuote.amountIn} ${currentQuote.fromSymbol} (${currentQuote.fromChain}) → ${currentQuote.amountOut} ${currentQuote.toSymbol} (${currentQuote.toChain})${hashSuffix}`,
          });
        } catch (mockErr) {
          console.error('Testnet swap error:', mockErr);
          setSwapResult({
            success: false,
            message: mockErr instanceof Error ? mockErr.message : 'Testnet swap failed',
          });
        }
        setSwapLoading(false);
        setCurrentQuote(null);
        setCurrentRoute(null);
        setSwap(s => ({ ...s, fromAmount: '', toAmount: '' }));
        return;
      }

      const provider = currentQuote.provider;

      // Handle different providers
      switch (provider) {
        case 'skipgo': {
          if (!currentRoute) {
            throw new Error('Skip Go route not available');
          }

          // Get user addresses for required chains
          const addresses = await sdk.getAddresses();
          const addressList: string[] = [];

          for (const chainId of currentRoute.requiredChainAddresses) {
            const chainName = Object.entries(CHAIN_ID_MAP).find(([, id]) => id === chainId)?.[0];
            if (!chainName) {
              throw new Error(`No address for chain: ${chainId}`);
            }
            const chainInfo = addresses.find(a => a.chain === chainName);
            if (!chainInfo) {
              throw new Error(`No wallet address for ${chainName}`);
            }
            addressList.push(chainInfo.address);
          }

          // Get transaction messages from Skip Go
          const messagesResponse = await skipGoApi.getMessages({
            sourceAssetDenom: currentRoute.sourceAssetDenom,
            sourceAssetChainId: currentRoute.sourceAssetChainId,
            destAssetDenom: currentRoute.destAssetDenom,
            destAssetChainId: currentRoute.destAssetChainId,
            amountIn: currentRoute.amountIn,
            amountOut: currentRoute.amountOut,
            addressList,
            operations: currentRoute.operations,
            slippageTolerancePercent: settings.slippage.toString(),
          });

          // Process each transaction
          for (const tx of messagesResponse.txs) {
            if (tx.evmTx) {
              const evmTx = tx.evmTx;

              // Handle ERC20 approvals if needed
              if (evmTx.requiredErc20Approvals && evmTx.requiredErc20Approvals.length > 0) {
                for (const approval of evmTx.requiredErc20Approvals) {
                  const approvalData = `0x095ea7b3${approval.spender.slice(2).padStart(64, '0')}${BigInt(approval.amount).toString(16).padStart(64, '0')}`;
                  await sdk.sendEVMTransaction({
                    transaction: {
                      to: approval.tokenContract,
                      data: approvalData,
                      chainId: parseInt(evmTx.chainId, 10),
                    },
                  });
                }
              }

              // Send the main transaction
              const result = await sdk.sendEVMTransaction({
                transaction: {
                  to: evmTx.to,
                  value: evmTx.value || '0x0',
                  data: evmTx.data,
                  chainId: parseInt(evmTx.chainId, 10),
                },
              });

              // Track the transaction
              await skipGoApi.waitForTransaction(result.transactionHash, evmTx.chainId, {
                onStatusUpdate: (status) => {
                  console.log('Swap status:', status.state);
                },
              });
            } else if (tx.cosmosTx) {
              throw new Error('Cosmos swaps not yet supported');
            } else if (tx.svmTx) {
              throw new Error('Solana swaps via Skip Go not yet supported');
            }
          }
          break;
        }

        case 'jupiter': {
          // Jupiter (Solana) swap execution
          if (!currentQuote.route) {
            throw new Error('Jupiter quote not available');
          }

          const jupiterQuote = currentQuote.route as import('./jupiterApi').JupiterQuote;

          // Get Solana address
          const addresses = await sdk.getAddresses();
          const solanaAddress = addresses.find(a => a.chainType === 'svm')?.address;
          if (!solanaAddress) {
            throw new Error('No Solana wallet address found');
          }

          // Get swap transaction from Jupiter
          const { jupiterApi } = await import('./jupiterApi');
          const swapTx = await jupiterApi.getSwapTransaction({
            quoteResponse: jupiterQuote,
            userPublicKey: solanaAddress,
          });

          // Sign the transaction via wallet bridge
          const signResult = await sdk.signRawSolanaTransaction({
            transaction: swapTx.swapTransaction,
            network: getSolanaSdkNetwork(),
          });

          // Send the signed transaction
          const solResult = await sdk.sendRawSolanaTransaction({
            signedTransaction: signResult.signedTransaction,
            network: getSolanaSdkNetwork(),
          });

          console.log('Jupiter swap result:', solResult);
          break;
        }

        case 'stonfi': {
          // STON.fi (TON) swap execution
          if (!currentQuote.route) {
            throw new Error('STON.fi quote not available');
          }

          const stonfiSimulation = currentQuote.route as import('./stonfiApi').StonfiSwapSimulation;

          // Get TON address
          const addresses = await sdk.getAddresses();
          const tonAddress = addresses.find(a => a.chainType === 'ton')?.address;
          if (!tonAddress) {
            throw new Error('No TON wallet address found');
          }

          // Build swap message using STON.fi API
          const { stonfiApi } = await import('./stonfiApi');
          const swapMessage = stonfiApi.buildSwapMessage({
            userWalletAddress: tonAddress,
            offerAddress: stonfiSimulation.offer_address,
            askAddress: stonfiSimulation.ask_address,
            offerUnits: stonfiSimulation.offer_units,
            minAskUnits: stonfiSimulation.min_ask_units,
          });

          // Sign the TON message via wallet bridge
          const signResult = await sdk.signRawTonMessage({
            to: swapMessage.to,
            value: swapMessage.value,
            payload: swapMessage.payload,
            network: getSdkNetwork(),
          });

          // Send the signed message
          const tonResult = await sdk.sendRawTonMessage({
            boc: signResult.boc,
            network: getSdkNetwork(),
          });

          console.log('STON.fi swap result:', tonResult);
          break;
        }

        case 'sunswap': {
          // SunSwap (TRON) swap execution
          if (!currentQuote.route) {
            throw new Error('SunSwap quote not available');
          }

          const sunswapRoute = currentQuote.route as import('./sunswapApi').SunswapRoute;

          // Get TRON address
          const addresses = await sdk.getAddresses();
          const tronAddress = addresses.find(a => a.chainType === 'tron')?.address;
          if (!tronAddress) {
            throw new Error('No TRON wallet address found');
          }

          // Build swap transaction using SunSwap API
          const { sunswapApi } = await import('./sunswapApi');
          const swapTx = sunswapApi.buildSwapTransaction({
            userAddress: tronAddress,
            tokenIn: sunswapRoute.tokenIn,
            tokenOut: sunswapRoute.tokenOut,
            amountIn: sunswapRoute.amountIn,
            minAmountOut: sunswapRoute.amountOut, // Should calculate with slippage
          });

          // Trigger and sign smart contract via wallet bridge
          const signResult = await sdk.triggerTronSmartContract({
            contractAddress: swapTx.to,
            functionSelector: 'swapExactTokensForTokens(uint256,uint256,address[],address,uint256)',
            parameter: '', // Would need proper ABI encoding in production
            callValue: swapTx.value === '0' ? 0 : parseInt(swapTx.value, 10),
            feeLimit: 100000000, // 100 TRX fee limit
            network: getTronSdkNetwork(),
          });

          // Broadcast the signed transaction
          const tronResult = await sdk.broadcastTronTransaction({
            signedTransaction: signResult.rawTransaction,
            network: getTronSdkNetwork(),
          });

          console.log('SunSwap swap result:', tronResult);
          break;
        }

        case 'bttc': {
          // BTTC Bridge (TRON <-> ETH/BSC) execution
          if (!currentQuote.route) {
            throw new Error('BTTC bridge quote not available');
          }

          const bridgeQuote = currentQuote.route as import('./bttcBridgeApi').BridgeQuote;

          // Get addresses for source and destination chains
          const addresses = await sdk.getAddresses();

          // Determine chain types
          const fromChainType = bridgeQuote.fromChain === 'tron' ? 'tron' : 'evm';
          const toChainType = bridgeQuote.toChain === 'tron' ? 'tron' : 'evm';

          const fromAddress = addresses.find(a => a.chainType === fromChainType)?.address;
          const toAddress = addresses.find(a => a.chainType === toChainType)?.address;

          if (!fromAddress || !toAddress) {
            throw new Error(`No wallet address found for ${!fromAddress ? bridgeQuote.fromChain : bridgeQuote.toChain}`);
          }

          // Import BTTC bridge constants
          const { BRIDGEABLE_TOKENS, BTTC_CONTRACTS } = await import('./bttcBridgeApi');

          // Get token info
          const tokenInfo = BRIDGEABLE_TOKENS[bridgeQuote.tokenSymbol.toUpperCase()];
          if (!tokenInfo) {
            throw new Error(`Token ${bridgeQuote.tokenSymbol} not supported by BTTC bridge`);
          }

          const tokenAddress = tokenInfo[bridgeQuote.fromChain as keyof typeof tokenInfo] as string;
          if (!tokenAddress) {
            throw new Error(`Token ${bridgeQuote.tokenSymbol} not available on ${bridgeQuote.fromChain}`);
          }

          const decimals = tokenInfo.decimals;
          const amountRaw = BigInt(Math.floor(parseFloat(bridgeQuote.amountIn) * Math.pow(10, decimals)));

          if (bridgeQuote.fromChain === 'tron') {
            // TRON -> BTTC/ETH/BSC bridge flow
            // Step 1: Approve token for bridge (predicate will spend tokens)
            const approveResult = await sdk.triggerTronSmartContract({
              contractAddress: tokenAddress,
              functionSelector: 'approve(address,uint256)',
              parameter: '', // Would need ABI encoding: predicate address + amount
              callValue: 0,
              feeLimit: 50000000, // 50 TRX
              network: getTronSdkNetwork(),
            });

            await sdk.broadcastTronTransaction({
              signedTransaction: approveResult.rawTransaction,
              network: getTronSdkNetwork(),
            });

            // Step 2: Deposit to bridge
            const depositResult = await sdk.triggerTronSmartContract({
              contractAddress: BTTC_CONTRACTS.tron.rootChainManager,
              functionSelector: 'depositFor(address,address,bytes)',
              parameter: '', // Would need ABI encoding: recipient, token, amount
              callValue: 0,
              feeLimit: 100000000, // 100 TRX
              network: getTronSdkNetwork(),
            });

            const tronBridgeResult = await sdk.broadcastTronTransaction({
              signedTransaction: depositResult.rawTransaction,
              network: getTronSdkNetwork(),
            });

            console.log('BTTC bridge deposit (TRON):', tronBridgeResult);
          } else {
            // ETH/BSC -> BTTC/TRON bridge flow
            const chainId = bridgeQuote.fromChain === 'ethereum' ? 1 : 56;
            const contracts = bridgeQuote.fromChain === 'ethereum'
              ? BTTC_CONTRACTS.ethereum
              : BTTC_CONTRACTS.bsc;

            // Step 1: Approve token for bridge
            const approveData = `0x095ea7b3${contracts.erc20Predicate.slice(2).padStart(64, '0')}${amountRaw.toString(16).padStart(64, '0')}`;
            await sdk.sendEVMTransaction({
              transaction: {
                to: tokenAddress,
                data: approveData,
                chainId,
              },
            });

            // Step 2: Deposit to bridge
            // depositFor(address user, address rootToken, bytes memory depositData)
            const depositData = `0xec7d0688${toAddress.slice(2).padStart(64, '0')}${tokenAddress.slice(2).padStart(64, '0')}${amountRaw.toString(16).padStart(64, '0')}`;
            const evmBridgeResult = await sdk.sendEVMTransaction({
              transaction: {
                to: contracts.rootChainManager,
                data: depositData,
                chainId,
              },
            });

            console.log('BTTC bridge deposit (EVM):', evmBridgeResult);
          }

          // Note: Bridge completion requires waiting for validators (~15-30 min)
          // and potentially claiming on the destination chain
          setSwapResult({
            success: true,
            message: `Bridge initiated: ${bridgeQuote.amountIn} ${bridgeQuote.tokenSymbol} from ${bridgeQuote.fromChain.toUpperCase()} to ${bridgeQuote.toChain.toUpperCase()}. Please wait ~${Math.ceil(bridgeQuote.estimatedTime / 60)} minutes for completion.`,
          });

          // Skip the normal success message
          setSwapLoading(false);
          setCurrentQuote(null);
          setCurrentRoute(null);
          setSwap(s => ({ ...s, fromAmount: '', toAmount: '' }));
          return;
        }

        case 'canton': {
          // Canton Bridge (Canton CC <-> Base USDC) execution
          if (!currentQuote.route) {
            throw new Error('Canton bridge quote not available');
          }

          const cantonQuote = currentQuote.route as import('./cantonBridgeApi').CantonBridgeQuote;
          const { CANTON_BRIDGEABLE_TOKENS } = await import('./cantonBridgeApi');

          // Get addresses
          const addresses = await sdk.getAddresses();
          const baseAddress = addresses.find(a => a.chain === 'Base')?.address;
          const partyId = user?.partyId;

          if (!baseAddress) {
            throw new Error('No Base wallet address found');
          }

          if (!partyId) {
            throw new Error('No Canton party ID found. Please ensure you are logged in.');
          }

          if (cantonQuote.bridgeData?.direction === 'cc_to_usdc') {
            // CC -> USDC: Lock CC on Canton via BridgeEscrow contract
            const { checkBridgeUsdcBalance } = await import('./cantonBridgeApi');

            // Check if bridge has sufficient USDC before locking CC
            const balanceCheck = await checkBridgeUsdcBalance(cantonQuote.amountOut);
            if (!balanceCheck.hasSufficient) {
              throw new Error(balanceCheck.error || `Bridge has insufficient USDC liquidity (${balanceCheck.bridgeBalance} available)`);
            }

            const templateId = CANTON_BRIDGEABLE_TOKENS.CC.canton.templateId;
            const now = new Date();
            const expiresAt = new Date(now.getTime() + 30 * 60 * 1000).toISOString(); // 30 min timeout

            // Create BridgeEscrow contract to lock CC
            const escrowResult = await sdk.cantonCreate({
              templateId,
              payload: {
                owner: partyId,
                bridgeOperator: partyId, // User is their own operator (backend handles actual releases)
                amount: cantonQuote.amountIn,
                destinationChain: 'base',
                destinationAddress: baseAddress,
                sourceChain: 'canton',
                nonce: cantonQuote.bridgeData.nonce,
                expiresAt,
                createdAt: now.toISOString(),
                status: 'Pending',
                releaseTxHash: null,
              },
            });

            console.log('Canton bridge escrow created:', escrowResult.contractId);

            setSwapResult({
              success: true,
              message: `Bridge initiated: ${cantonQuote.amountIn} CC locked in escrow. ${cantonQuote.amountOut} USDC will arrive on Base in ~${Math.ceil(cantonQuote.estimatedTime / 60)} minutes.`,
            });

          } else {
            // USDC -> CC: Deposit USDC on Base, then create BridgeRequest on Canton
            const { cantonBridgeApi } = await import('./cantonBridgeApi');
            const bridgeParams = cantonBridgeApi.buildUSDCToCCParams(
              cantonQuote,
              baseAddress,
              partyId
            );

            // Step 1: Send USDC to bridge contract on Base
            const depositResult = await sdk.sendEVMTransaction({
              transaction: {
                to: bridgeParams.evmDeposit.to,
                value: bridgeParams.evmDeposit.value,
                data: bridgeParams.evmDeposit.data,
                chainId: bridgeParams.evmDeposit.chainId,
              },
            });

            console.log('Canton bridge USDC deposit:', depositResult.transactionHash);

            // Step 2: Create BridgeRequest on Canton to request CC release
            const templateId = CANTON_BRIDGEABLE_TOKENS.USDC.canton.templateId;

            const requestResult = await sdk.cantonCreate({
              templateId,
              payload: {
                recipient: partyId,
                bridgeOperator: partyId, // User is their own operator (backend handles actual releases)
                evmSourceAddress: baseAddress,
                evmTxHash: depositResult.transactionHash,
                usdcAmount: bridgeParams.cantonParams.usdcAmount,
                requestedCCAmount: bridgeParams.cantonParams.requestedCCAmount,
                createdAt: new Date().toISOString(),
                status: 'Pending',
              },
            });

            console.log('Canton bridge request created:', requestResult.contractId);

            setSwapResult({
              success: true,
              message: `Bridge initiated: ${cantonQuote.amountIn} USDC deposited (tx: ${depositResult.transactionHash.slice(0, 10)}...). ${cantonQuote.amountOut} CC will arrive on Canton in ~${Math.ceil(cantonQuote.estimatedTime / 60)} minutes.`,
            });
          }

          // Skip the normal success message
          setSwapLoading(false);
          setCurrentQuote(null);
          setCurrentRoute(null);
          setSwap(s => ({ ...s, fromAmount: '', toAmount: '' }));
          return;
        }

        default:
          throw new Error(`Unknown swap provider: ${provider}`);
      }

      setSwapResult({
        success: true,
        message: `Swapped ${swap.fromAmount} ${swap.fromToken} for ${swap.toAmount} ${swap.toToken} via ${getProviderName(provider)}`,
      });

      // Refresh assets
      const assetsData = await sdk.getAssets();
      setAssets(assetsData);

      // Clear form and quote
      setSwap(s => ({ ...s, fromAmount: '', toAmount: '' }));
      setCurrentQuote(null);
      setCurrentRoute(null);

    } catch (err) {
      console.error('Swap error:', err);
      setSwapResult({
        success: false,
        message: err instanceof Error ? err.message : 'Swap failed',
      });
    } finally {
      setSwapLoading(false);
    }
  };

  const handleMaxClick = () => {
    const balance = getChainBalance(swap.fromToken, swap.fromChain);
    handleFromAmountChange(balance.toString());
  };

  // Filter assets by search query
  // For multi-chain assets, don't exclude - they can swap across chains (e.g., USDC Base -> USDC Solana)
  // For single-chain assets, exclude if same symbol is selected on the other side
  const getFilteredAssets = (excludeSymbol?: string) => {
    return assets.filter(asset => {
      // For single-chain assets, exclude if it's the exact same token
      if (!hasMultipleChains(asset) && excludeSymbol && asset.symbol === excludeSymbol) {
        return false;
      }
      if (!searchQuery) return true;
      return (
        asset.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
        asset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (asset.chain || '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    });
  };

  // Get available chains for selection, excluding the chain already selected on the other side
  const getSelectableChains = (asset: Asset, excludeSymbol?: string, excludeChain?: string) => {
    const chains = getAssetChains(asset);
    // If this asset is the same as the excluded one, filter out the excluded chain
    if (excludeSymbol && asset.symbol === excludeSymbol && excludeChain) {
      return chains.filter(c => c.chain !== excludeChain);
    }
    return chains;
  };

  // Handle chain selection from sub-menu
  const handleChainSelect = (symbol: string, chain: string, target: 'from' | 'to') => {
    if (target === 'from') {
      setSwap(s => ({ ...s, fromToken: symbol, fromChain: chain }));
      setShowFromTokenList(false);
    } else {
      setSwap(s => ({ ...s, toToken: symbol, toChain: chain }));
      setShowToTokenList(false);
    }
    setExpandedAsset(null);
    if (swap.fromAmount) handleFromAmountChange(swap.fromAmount);
  };

  const getPoolInfo = () => {
    const poolKey = `${swap.fromToken}-${swap.toToken}`;
    const reversePoolKey = `${swap.toToken}-${swap.fromToken}`;
    return LIQUIDITY_POOLS[poolKey] || LIQUIDITY_POOLS[reversePoolKey] || null;
  };

  const formatUsd = (value: number): string => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
    return `$${value.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Connecting...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-icon">!</div>
        <h2>Connection Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!connected || !user) {
    return (
      <div className="not-connected">
        <h2>Not Connected</h2>
        <p>Please log in to the portal to use Swap.</p>
      </div>
    );
  }

  const priceImpact = swap.fromAmount
    ? getPriceImpact(parseFloat(swap.fromAmount), swap.fromToken, swap.toToken)
    : 0;

  const truncatePartyId = (partyId: string): string => {
    if (partyId.length <= 20) return partyId;
    return `${partyId.substring(0, 12)}...${partyId.substring(partyId.length - 8)}`;
  };

  return (
    <div className="app">
      {testnetMode && (
        <div className="testnet-banner-wrapper">
          <div className="testnet-banner" onClick={() => setShowTestnetInfo(!showTestnetInfo)} style={{ cursor: 'pointer' }}>
            TESTNET MODE
            <span className="testnet-toggle">{showTestnetInfo ? '▲' : '▼'}</span>
          </div>
          {showTestnetInfo && (
            <div className="testnet-info-panel">
              <div className="testnet-info-section">
                <span className="testnet-info-label">Pool Contract</span>
                <div className="testnet-info-row">
                  <span className="testnet-chain-tag">Base Sepolia</span>
                  <a href={`https://sepolia.basescan.org/address/${POOL_ADDRESSES[84532]}`} target="_blank" rel="noopener noreferrer">{POOL_ADDRESSES[84532].slice(0, 8)}...{POOL_ADDRESSES[84532].slice(-6)}</a>
                </div>
                <div className="testnet-info-row">
                  <span className="testnet-chain-tag">Eth Sepolia</span>
                  <a href={`https://sepolia.etherscan.io/address/${POOL_ADDRESSES[11155111]}`} target="_blank" rel="noopener noreferrer">{POOL_ADDRESSES[11155111].slice(0, 8)}...{POOL_ADDRESSES[11155111].slice(-6)}</a>
                </div>
              </div>
              <div className="testnet-info-section">
                <span className="testnet-info-label">USDC Token</span>
                <div className="testnet-info-row">
                  <span className="testnet-chain-tag">Base Sepolia</span>
                  <a href={`https://sepolia.basescan.org/address/${TESTNET_TOKENS[84532]?.USDC}`} target="_blank" rel="noopener noreferrer">{TESTNET_TOKENS[84532]?.USDC.slice(0, 8)}...{TESTNET_TOKENS[84532]?.USDC.slice(-6)}</a>
                </div>
                <div className="testnet-info-row">
                  <span className="testnet-chain-tag">Eth Sepolia</span>
                  <a href={`https://sepolia.etherscan.io/address/${TESTNET_TOKENS[11155111]?.USDC}`} target="_blank" rel="noopener noreferrer">{TESTNET_TOKENS[11155111]?.USDC.slice(0, 8)}...{TESTNET_TOKENS[11155111]?.USDC.slice(-6)}</a>
                </div>
              </div>
              <div className="testnet-info-section">
                <span className="testnet-info-label">Deployer</span>
                <div className="testnet-info-row">
                  <span className="testnet-chain-tag">Base</span>
                  <a href="https://sepolia.basescan.org/address/0xfE0635C74703D7E641eFE87FC41773Fe8E9f2636" target="_blank" rel="noopener noreferrer">0xfE0635...2636</a>
                </div>
                <div className="testnet-info-row">
                  <span className="testnet-chain-tag">Eth</span>
                  <a href="https://sepolia.etherscan.io/address/0xfE0635C74703D7E641eFE87FC41773Fe8E9f2636" target="_blank" rel="noopener noreferrer">0xfE0635...2636</a>
                </div>
              </div>
              <div className="testnet-info-section" style={{ flexBasis: '100%' }}>
                <span className="testnet-info-label">Bridge Operator (Canton)</span>
                {bridgeParty ? (
                  <>
                    <div className="testnet-info-row" style={{ gap: '8px' }}>
                      <span className="testnet-chain-tag">Party ID</span>
                      <span style={{ fontSize: '0.7rem', wordBreak: 'break-all', fontFamily: 'monospace', color: '#a3e635', flex: 1 }}>
                        {bridgeParty}
                      </span>
                      <button
                        style={{ padding: '2px 8px', fontSize: '0.65rem', background: '#333', color: '#fff', border: '1px solid #555', borderRadius: '4px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        onClick={() => { navigator.clipboard.writeText(bridgeParty); }}
                      >Copy</button>
                    </div>
                    <div className="testnet-info-row" style={{ gap: '8px', marginTop: '2px' }}>
                      <span className="testnet-chain-tag">Status</span>
                      <span style={{ fontSize: '0.75rem', color: '#a3e635' }}>Provisioned</span>
                      <span style={{ fontSize: '0.7rem', color: '#888' }}>actAs/readAs granted to wallet users</span>
                    </div>
                  </>
                ) : (
                  <div className="testnet-info-row">
                    <span style={{ fontSize: '0.72rem', color: '#ef4444' }}>Not configured</span>
                  </div>
                )}
              </div>
              <div className="testnet-info-section" style={{ flexBasis: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <span className="testnet-info-label" style={{ marginBottom: 0 }}>Vault Balances</span>
                  <button
                    onClick={refreshVaultBalances}
                    disabled={balancesLoading}
                    style={{ padding: '2px 10px', fontSize: '0.7rem', background: balancesLoading ? '#555' : '#333', color: '#fff', border: '1px solid #555', borderRadius: '4px', cursor: balancesLoading ? 'wait' : 'pointer' }}
                  >{balancesLoading ? 'Loading...' : 'Refresh'}</button>
                </div>
                {poolBalances.length === 0 && !balancesLoading && (
                  <div className="testnet-info-row" style={{ color: '#888' }}>
                    <span>Click Refresh to load pool balances</span>
                  </div>
                )}
                {poolBalances.map(pb => (
                  <div className="testnet-info-row" key={pb.chainId} style={{ gap: '12px' }}>
                    <span className="testnet-chain-tag" style={{ minWidth: '80px' }}>{pb.chainName}</span>
                    <span style={{ color: parseFloat(pb.ethBalance) < 0.01 ? '#ef4444' : '#a3e635' }}>ETH: {pb.ethBalance}</span>
                    <span style={{ color: parseFloat(pb.usdcBalance) < 10 ? '#ef4444' : '#a3e635' }}>USDC: {pb.usdcBalance}</span>
                  </div>
                ))}
                <div className="testnet-info-row" key="canton-vault" style={{ gap: '12px' }}>
                  <span className="testnet-chain-tag" style={{ minWidth: '80px' }}>Canton</span>
                  <span style={{ color: cantonVault.ccBalance === '—' || cantonVault.ccBalance === '?' ? '#888' : '#a3e635' }}>CC: {cantonVault.ccBalance}</span>
                  <span style={{ color: cantonVault.usdcBalance === '0' || cantonVault.usdcBalance === '?' || cantonVault.usdcBalance === '—' || cantonVault.usdcBalance === 'no template' ? '#ef4444' : '#a3e635' }}>USDC: {cantonVault.usdcBalance}</span>
                </div>
                {bridgeContracts.length > 0 && (
                  <div className="testnet-info-row" style={{ gap: '12px', marginTop: '4px' }}>
                    <span className="testnet-chain-tag" style={{ minWidth: '80px' }}>Bridge</span>
                    <span>{bridgeContracts.filter(c => c.status === 'Pending').length} pending</span>
                    <span style={{ color: '#888' }}>{bridgeContracts.length} total contracts</span>
                  </div>
                )}
              </div>
              <div className="testnet-info-section">
                <span className="testnet-info-label">Bridge Auto-Accept</span>
                <div className="testnet-info-row">
                  <span style={{ fontSize: '0.72rem', color: autoAcceptStatus.startsWith('Error') ? '#ef4444' : '#a3e635' }}>
                    {autoAcceptStatus || 'Starting...'}
                  </span>
                </div>
              </div>
              <div className="testnet-info-section">
                <span className="testnet-info-label">Faucets</span>
                <div className="testnet-info-row">
                  <a href="https://faucet.circle.com" target="_blank" rel="noopener noreferrer">Circle USDC Faucet</a>
                </div>
                <div className="testnet-info-row">
                  <a href="https://www.alchemy.com/faucets/base-sepolia" target="_blank" rel="noopener noreferrer">Base Sepolia ETH</a>
                  <a href="https://www.alchemy.com/faucets/ethereum-sepolia" target="_blank" rel="noopener noreferrer">Eth Sepolia ETH</a>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="swap-container">
        {/* Header */}
        <div className="swap-header">
          <div className="header-left">
            <h1>Swap</h1>
            {user?.partyId && (
              <div className="party-id-badge" title={user.partyId}>
                <span className="party-label">Canton Party:</span>
                <span className="party-value">{truncatePartyId(user.partyId)}</span>
              </div>
            )}
          </div>
          <button
            className={`settings-btn ${showSettings ? 'active' : ''}`}
            onClick={() => setShowSettings(!showSettings)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3" />
              <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" />
            </svg>
          </button>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="settings-panel">
            <div className="settings-row">
              <span>Slippage Tolerance</span>
              <div className="slippage-options">
                {[0.1, 0.5, 1.0].map(val => (
                  <button
                    key={val}
                    className={`slippage-btn ${settings.slippage === val ? 'active' : ''}`}
                    onClick={() => setSettings(s => ({ ...s, slippage: val }))}
                  >
                    {val}%
                  </button>
                ))}
                <div className="slippage-custom">
                  <input
                    type="number"
                    value={settings.slippage}
                    onChange={(e) => setSettings(s => ({ ...s, slippage: parseFloat(e.target.value) || 0.5 }))}
                    step="0.1"
                    min="0.1"
                    max="50"
                  />
                  <span>%</span>
                </div>
              </div>
            </div>
            <div className="settings-row">
              <span>Transaction Deadline</span>
              <div className="deadline-input">
                <input
                  type="number"
                  value={settings.deadline}
                  onChange={(e) => setSettings(s => ({ ...s, deadline: parseInt(e.target.value) || 20 }))}
                  min="1"
                  max="60"
                />
                <span>minutes</span>
              </div>
            </div>
          </div>
        )}

        {/* From Token */}
        <div className="swap-input-container">
          <div className="swap-input-header">
            <span>You Pay</span>
            <span className="balance" onClick={handleMaxClick}>
              Balance: {getChainBalance(swap.fromToken, swap.fromChain).toLocaleString(undefined, { maximumFractionDigits: 4 })}
              <button className="max-btn">MAX</button>
            </span>
          </div>
          <div className="swap-input-row">
            <input
              type="number"
              placeholder="0.0"
              value={swap.fromAmount}
              onChange={(e) => handleFromAmountChange(e.target.value)}
              className="swap-amount-input"
            />
            <button
              className="token-select-btn"
              onClick={() => {
                setShowFromTokenList(!showFromTokenList);
                setShowToTokenList(false);
                setSearchQuery('');
                setExpandedAsset(null);
              }}
            >
              {swap.fromToken ? (
                <>
                  <TokenIcon symbol={swap.fromToken} size={28} />
                  <div className="token-select-info">
                    <span>{swap.fromToken}</span>
                    {swap.fromChain && <span className="token-chain-badge">{swap.fromChain}</span>}
                  </div>
                </>
              ) : (
                <span>Select</span>
              )}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          </div>
          {swap.fromAmount && (
            <div className="usd-value">
              ~{formatUsd(parseFloat(swap.fromAmount) * (BASE_RATES[swap.fromToken] || 1))}
            </div>
          )}
          {showFromTokenList && (
            <div className="token-list">
              <div className="token-search">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  placeholder="Search token or chain..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
              </div>
              {assets.length === 0 ? (
                <div className="token-list-empty">
                  <p>No tokens tracked in wallet</p>
                </div>
              ) : (
                <div className="token-list-section">
                  <span className="section-label">Available Tokens</span>
                  {getFilteredAssets(swap.toToken).map((asset) => {
                    const selectableChains = getSelectableChains(asset, swap.toToken, swap.toChain);
                    const showAsMultiChain = selectableChains.length > 1;

                    return (
                      <div key={asset.symbol} className="token-item-wrapper">
                        <div
                          className={`token-list-item ${swap.fromToken === asset.symbol ? 'selected' : ''} ${showAsMultiChain ? 'has-chains' : ''}`}
                          onClick={() => {
                            if (showAsMultiChain) {
                              setExpandedAsset(expandedAsset === asset.symbol ? null : asset.symbol);
                            } else if (selectableChains.length === 1) {
                              setSwap(s => ({ ...s, fromToken: asset.symbol, fromChain: selectableChains[0].chain }));
                              setShowFromTokenList(false);
                              setExpandedAsset(null);
                              if (swap.fromAmount) handleFromAmountChange(swap.fromAmount);
                            }
                          }}
                        >
                          <TokenIcon symbol={asset.symbol} size={36} />
                          <div className="token-info">
                            <span className="token-symbol">{asset.symbol}</span>
                            <span className="token-name">{asset.name}</span>
                          </div>
                          {showAsMultiChain ? (
                            <div className="token-multichain-indicator">
                              <span className="chain-count">{selectableChains.length} chains</span>
                              <svg className={`expand-icon ${expandedAsset === asset.symbol ? 'expanded' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="6 9 12 15 18 9" />
                              </svg>
                            </div>
                          ) : (
                            <>
                              <div className="token-chain-info">
                                <span className="token-chain">{selectableChains[0]?.chain || asset.chain || 'Unknown'}</span>
                              </div>
                              <div className="token-balance-info">
                                <span className="token-balance">{(selectableChains[0]?.balance ?? asset.balance).toLocaleString(undefined, { maximumFractionDigits: 4 })}</span>
                                <span className="token-usd">{formatUsd((selectableChains[0]?.balance ?? asset.balance) * (BASE_RATES[asset.symbol] || 1))}</span>
                              </div>
                            </>
                          )}
                        </div>
                        {/* Chain sub-menu for multi-chain assets */}
                        {showAsMultiChain && expandedAsset === asset.symbol && (
                          <div className="chain-submenu">
                            {selectableChains.map((chainInfo) => (
                              <div
                                key={chainInfo.chain}
                                className={`chain-option ${swap.fromToken === asset.symbol && swap.fromChain === chainInfo.chain ? 'selected' : ''}`}
                                onClick={() => handleChainSelect(asset.symbol, chainInfo.chain, 'from')}
                              >
                                <span className="chain-name">{chainInfo.chain}</span>
                                <div className="chain-balance-info">
                                  <span className="chain-balance">{chainInfo.balance.toLocaleString(undefined, { maximumFractionDigits: 4 })}</span>
                                  <span className="chain-usd">{formatUsd(chainInfo.balance * (BASE_RATES[asset.symbol] || 1))}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Swap Direction Button */}
        <div className="swap-direction">
          <button className="swap-direction-btn" onClick={handleSwapTokens}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M7 16V4M7 4L3 8M7 4l4 4M17 8v12M17 20l4-4M17 20l-4-4" />
            </svg>
          </button>
        </div>

        {/* To Token */}
        <div className="swap-input-container">
          <div className="swap-input-header">
            <span>You Receive</span>
            <span className="balance">
              Balance: {getChainBalance(swap.toToken, swap.toChain).toLocaleString(undefined, { maximumFractionDigits: 4 })}
            </span>
          </div>
          <div className="swap-input-row">
            <input
              type="number"
              placeholder={quoteLoading ? 'Loading...' : '0.0'}
              value={swap.toAmount}
              onChange={(e) => handleToAmountChange(e.target.value)}
              className={`swap-amount-input ${quoteLoading ? 'loading' : ''}`}
              readOnly={quoteLoading}
            />
            <button
              className="token-select-btn"
              onClick={() => {
                setShowToTokenList(!showToTokenList);
                setShowFromTokenList(false);
                setSearchQuery('');
                setExpandedAsset(null);
              }}
            >
              {swap.toToken ? (
                <>
                  <TokenIcon symbol={swap.toToken} size={28} />
                  <div className="token-select-info">
                    <span>{swap.toToken}</span>
                    {swap.toChain && <span className="token-chain-badge">{swap.toChain}</span>}
                  </div>
                </>
              ) : (
                <span>Select</span>
              )}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          </div>
          {quoteError && swap.fromAmount && (
            <div className="quote-error">
              {quoteError}
            </div>
          )}
          {swap.toAmount && !quoteError && (
            <div className="usd-value">
              ~{formatUsd(parseFloat(swap.toAmount) * (BASE_RATES[swap.toToken] || 1))}
            </div>
          )}
          {/* Quote Route & Provider Info */}
          {currentQuote && !quoteError && (
            <div className="quote-route-info">
              <div className="route-provider">
                <span className="provider-badge">{getProviderName(currentQuote.provider)}</span>
                <span className="route-path">
                  {swap.fromChain} {swap.fromToken} → {swap.toChain} {swap.toToken}
                </span>
              </div>
              {currentQuote.estimatedTime && (
                <span className="route-time">~{Math.ceil(currentQuote.estimatedTime / 60)} min</span>
              )}
            </div>
          )}
          {quoteLoading && (
            <div className="quote-route-info loading">
              <span className="loading-text">Finding best route...</span>
            </div>
          )}
          {showToTokenList && (
            <div className="token-list">
              <div className="token-search">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  placeholder="Search token or chain..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
              </div>
              {assets.length === 0 ? (
                <div className="token-list-empty">
                  <p>No tokens tracked in wallet</p>
                </div>
              ) : (
                <div className="token-list-section">
                  <span className="section-label">Available Tokens</span>
                  {getFilteredAssets(swap.fromToken).map((asset) => {
                    const selectableChains = getSelectableChains(asset, swap.fromToken, swap.fromChain);
                    const showAsMultiChain = selectableChains.length > 1;

                    return (
                      <div key={asset.symbol} className="token-item-wrapper">
                        <div
                          className={`token-list-item ${swap.toToken === asset.symbol ? 'selected' : ''} ${showAsMultiChain ? 'has-chains' : ''}`}
                          onClick={() => {
                            if (showAsMultiChain) {
                              setExpandedAsset(expandedAsset === asset.symbol ? null : asset.symbol);
                            } else if (selectableChains.length === 1) {
                              setSwap(s => ({ ...s, toToken: asset.symbol, toChain: selectableChains[0].chain }));
                              setShowToTokenList(false);
                              setExpandedAsset(null);
                              if (swap.fromAmount) handleFromAmountChange(swap.fromAmount);
                            }
                          }}
                        >
                          <TokenIcon symbol={asset.symbol} size={36} />
                          <div className="token-info">
                            <span className="token-symbol">{asset.symbol}</span>
                            <span className="token-name">{asset.name}</span>
                          </div>
                          {showAsMultiChain ? (
                            <div className="token-multichain-indicator">
                              <span className="chain-count">{selectableChains.length} chains</span>
                              <svg className={`expand-icon ${expandedAsset === asset.symbol ? 'expanded' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="6 9 12 15 18 9" />
                              </svg>
                            </div>
                          ) : (
                            <>
                              <div className="token-chain-info">
                                <span className="token-chain">{selectableChains[0]?.chain || asset.chain || 'Unknown'}</span>
                              </div>
                              <div className="token-balance-info">
                                <span className="token-balance">{(selectableChains[0]?.balance ?? asset.balance).toLocaleString(undefined, { maximumFractionDigits: 4 })}</span>
                                <span className="token-usd">{formatUsd((selectableChains[0]?.balance ?? asset.balance) * (BASE_RATES[asset.symbol] || 1))}</span>
                              </div>
                            </>
                          )}
                        </div>
                        {/* Chain sub-menu for multi-chain assets */}
                        {showAsMultiChain && expandedAsset === asset.symbol && (
                          <div className="chain-submenu">
                            {selectableChains.map((chainInfo) => (
                              <div
                                key={chainInfo.chain}
                                className={`chain-option ${swap.toToken === asset.symbol && swap.toChain === chainInfo.chain ? 'selected' : ''}`}
                                onClick={() => handleChainSelect(asset.symbol, chainInfo.chain, 'to')}
                              >
                                <span className="chain-name">{chainInfo.chain}</span>
                                <div className="chain-balance-info">
                                  <span className="chain-balance">{chainInfo.balance.toLocaleString(undefined, { maximumFractionDigits: 4 })}</span>
                                  <span className="chain-usd">{formatUsd(chainInfo.balance * (BASE_RATES[asset.symbol] || 1))}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Exchange Rate & Route */}
        {swap.fromToken && swap.toToken && (
          <div className="swap-info">
            <div className="exchange-rate">
              <span className="rate-label">Rate</span>
              <span className="rate-value">
                1 {swap.fromToken} = {getExchangeRate(swap.fromToken, swap.toToken).toFixed(6)} {swap.toToken}
              </span>
            </div>
            {getPoolInfo() && (
              <div className="pool-info">
                <span className="pool-label">Pool</span>
                <span className="pool-value">
                  {swap.fromToken}/{swap.toToken} • TVL: {formatUsd(getPoolInfo()!.tvl)} • APR: {getPoolInfo()!.apr}%
                </span>
              </div>
            )}
          </div>
        )}

        {/* Swap Button */}
        <button
          className={`swap-btn ${swapLoading ? 'loading' : ''} ${quoteError ? 'error' : ''} ${priceImpact > 5 ? 'warning' : ''}`}
          onClick={handleSwap}
          disabled={!swap.fromToken || !swap.toToken || !swap.fromAmount || swapLoading || quoteLoading || !!quoteError || parseFloat(swap.fromAmount) > getChainBalance(swap.fromToken, swap.fromChain)}
        >
          {swapLoading ? (
            <div className="btn-spinner" />
          ) : quoteLoading ? (
            'Getting quote...'
          ) : !swap.fromToken || !swap.toToken ? (
            'Select tokens'
          ) : !swap.fromAmount ? (
            'Enter amount'
          ) : quoteError ? (
            quoteError
          ) : parseFloat(swap.fromAmount) > getChainBalance(swap.fromToken, swap.fromChain) ? (
            'Insufficient balance'
          ) : priceImpact > 5 ? (
            `Swap anyway (${priceImpact.toFixed(2)}% impact)`
          ) : (
            'Swap'
          )}
        </button>

        {/* Result Message */}
        {swapResult && (
          <div className={`swap-result ${swapResult.success ? 'success' : 'error'}`}>
            {swapResult.success && <span className="result-icon">✓</span>}
            {swapResult.message}
          </div>
        )}

        {/* Swap Details */}
        {swap.fromAmount && swap.toAmount && !quoteError && currentQuote && (
          <div className="swap-details">
            <div className="detail-row">
              <span>
                Price Impact
                {(currentQuote.priceImpact ? parseFloat(currentQuote.priceImpact) : priceImpact) > 3 && <span className="warning-badge">!</span>}
              </span>
              <span className={`detail-value ${(currentQuote.priceImpact ? parseFloat(currentQuote.priceImpact) : priceImpact) > 3 ? 'warning' : (currentQuote.priceImpact ? parseFloat(currentQuote.priceImpact) : priceImpact) > 1 ? 'caution' : ''}`}>
                {currentQuote.priceImpact ? parseFloat(currentQuote.priceImpact).toFixed(2) : priceImpact.toFixed(2)}%
              </span>
            </div>
            <div className="detail-row">
              <span>Max Slippage</span>
              <span className="detail-value">{settings.slippage}%</span>
            </div>
            {currentQuote.estimatedFees && (
              <div className="detail-row">
                <span>Network Fee</span>
                <span className="detail-value">~${currentQuote.estimatedFees}</span>
              </div>
            )}
            <div className="detail-row">
              <span>Minimum Received</span>
              <span className="detail-value highlight">
                {(parseFloat(swap.toAmount) * (1 - settings.slippage / 100)).toFixed(6)} {swap.toToken}
              </span>
            </div>
            {currentQuote.estimatedTime && (
              <div className="detail-row">
                <span>Est. Time</span>
                <span className="detail-value">
                  ~{Math.ceil(currentQuote.estimatedTime / 60)} min
                </span>
              </div>
            )}
            <div className="detail-row">
              <span>Provider</span>
              <span className="detail-value">{getProviderName(currentQuote.provider)}</span>
            </div>
            <div className="detail-row">
              <span>Route</span>
              <span className="detail-value route">
                {swap.fromChain && `${swap.fromChain} `}{swap.fromToken} → {swap.toChain && `${swap.toChain} `}{swap.toToken}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
